/**
 * @fileoverview Dashboard Geral — copia exata do Dashboard padrao, porem
 * escopada ao grupo (groupName) do usuario autenticado. Todas as chamadas
 * a /dashboard sao roteadas via apiGroup, que injeta groupName em params.
 */
import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimerIcon from "@material-ui/icons/Timer";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { toast } from "react-toastify";
import {
  AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import NoResponseOffendersModal from "../../components/NoResponseOffendersModal";
import MainContainer from "../../components/MainContainer";
import ModernSelect from "../../components/ModernSelect";
import api from "../../services/api";
import { makeApiGroup } from "./apiGroup";
import moment from "moment";
import "moment/locale/pt-br";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ptBR from "date-fns/locale/pt-BR";
import { ChartsMessagesPerUser } from "../Dashboard/ChartsMessagesPerUser";
import ChartsHeatmap from "../Dashboard/ChartsHeatmap";
import { i18n } from "../../translate/i18n";
import { X, Circle, MessageCircle } from "lucide-react";
import MessageStats from "../../components/MessageStats";
moment.locale("pt-br");

const getDashboardThemeTokens = (theme) => {
  const isDark = theme.palette.type === "dark";

  return {
    isDark,
    cardBackground: isDark ? "rgba(14,16,22,0.85)" : "#ffffff",
    cardBackgroundStrong: isDark ? "rgba(14,16,22,0.98)" : "#ffffff",
    cardBorder: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.12)",
    cardBorderSoft: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.08)",
    hoverBorder: isDark ? "rgba(99,102,241,0.2)" : "rgba(59,130,246,0.24)",
    hoverBorderStrong: isDark ? "rgba(99,102,241,0.25)" : "rgba(59,130,246,0.28)",
    hoverShadow: isDark ? "0 0 16px rgba(99,102,241,0.06)" : "0 10px 24px rgba(15,23,42,0.08)",
    hoverShadowStrong: isDark ? "0 0 20px rgba(99,102,241,0.08)" : "0 12px 28px rgba(15,23,42,0.1)",
    textPrimary: isDark ? "#e8eaed" : "#0f172a",
    textSecondary: isDark ? "#9ca3af" : "#475569",
    textMuted: isDark ? "#6b7280" : "#64748b",
    textSubtle: isDark ? "#4b5563" : "#94a3b8",
    link: isDark ? "#818cf8" : "#2563eb",
    surface: isDark ? "rgba(255,255,255,0.04)" : "rgba(248,250,252,0.96)",
    chipBackground: isDark ? "rgba(99,102,241,0.15)" : "rgba(59,130,246,0.1)",
    chipColor: isDark ? "#a5b4fc" : "#1d4ed8",
    chipBorder: isDark ? "rgba(99,102,241,0.25)" : "rgba(59,130,246,0.18)",
    chipBackgroundActive: isDark ? "rgba(99,102,241,0.4)" : "rgba(59,130,246,0.16)",
    chipColorActive: isDark ? "#e0e7ff" : "#1e3a8a",
    chipBorderActive: isDark ? "rgba(99,102,241,0.6)" : "rgba(59,130,246,0.26)",
    alertBackground: isDark ? "rgba(244,63,94,0.12)" : "rgba(239,68,68,0.1)",
    alertColor: isDark ? "#fca5a5" : "#b91c1c",
    alertBorder: isDark ? "rgba(244,63,94,0.25)" : "rgba(239,68,68,0.18)",
    helperBorder: isDark ? "rgba(148,163,184,0.3)" : "rgba(148,163,184,0.45)",
    calendarBackground: isDark ? "rgba(15,15,17,0.98)" : "#ffffff",
    calendarText: isDark ? "#e5e7eb" : "#0f172a",
    calendarBorder: isDark ? "rgba(91,124,255,0.18)" : "rgba(15,23,42,0.12)",
    calendarHeaderBackground: isDark
      ? "linear-gradient(135deg, rgba(91,124,255,0.1), rgba(29,78,216,0.06))"
      : "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(14,165,233,0.05))",
    calendarHeaderBorder: isDark ? "rgba(91,124,255,0.15)" : "rgba(59,130,246,0.12)",
    calendarWeekday: isDark ? "#94a3b8" : "#64748b",
    calendarRange: isDark ? "rgba(59,130,246,0.18)" : "rgba(59,130,246,0.14)",
    scrollbarThumb: isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.16)",
    tooltipBackground: isDark ? "rgba(0,0,0,0.92)" : "rgba(15,23,42,0.94)",
    tooltipBorder: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.16)",
    chartGrid: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.08)",
    rowHover: isDark ? "rgba(255,255,255,0.06)" : "rgba(59,130,246,0.08)",
    emptyPie: isDark ? "#1f2937" : "#cbd5e1",
    dialogDivider: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.08)",
    dialogShadow: isDark ? "0 18px 40px rgba(0,0,0,0.35)" : "0 18px 40px rgba(15,23,42,0.12)",
  };
};

const useStyles = makeStyles((theme) => {
  const colors = getDashboardThemeTokens(theme);

  return ({
  page: {
    backgroundColor: colors.isDark ? "#0A0A0A" : "#f8f9fa",
  },
  root: {
    padding: 10,
    minHeight: "100%",
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: colors.isDark ? "#0A0A0A" : "#f8f9fa",
    [theme.breakpoints.down("sm")]: { padding: 6, paddingBottom: 80 },
  },
  // Sentinela pro bug do Chromium em flex column + overflow-y auto:
  // padding/margin no fim do conteúdo não entra na altura scrollável.
  // Um div com height explícito é conteúdo real e é contado.
  bottomSpacer: { height: 32, flexShrink: 0 },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  title: { fontSize: 18, fontWeight: 700, color: colors.textPrimary },
  emptyState: {
    padding: 40,
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 14,
    background: colors.cardBackground,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 8,
    marginTop: 12,
  },
  /* ── Master grid ── */
  master: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(60, 1fr)",
    // Helpers para legibilidade: cada métrica ocupa 12u (5×12 = 60).
    // heat=40, pie=20; filt=60; hist=30, msgs=30; arthist=40, artrank=20.
    gridTemplateAreas: `
      "${"m1 ".repeat(12).trim()} ${"m2 ".repeat(12).trim()} ${"m3 ".repeat(12).trim()} ${"m4 ".repeat(12).trim()} ${"m5 ".repeat(12).trim()}"
      "${"heat ".repeat(40).trim()} ${"pie ".repeat(20).trim()}"
      "${"filt ".repeat(60).trim()}"
      "${"hist ".repeat(30).trim()} ${"msgs ".repeat(30).trim()}"
      "${"arthist ".repeat(40).trim()} ${"artrank ".repeat(20).trim()}"
    `,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr 1fr",
      gridTemplateAreas: `
        "m1 m2"
        "m3 m4"
        "m5 m5"
        "heat heat"
        "pie pie"
        "filt filt"
        "hist hist"
        "msgs msgs"
        "arthist arthist"
        "artrank artrank"
      `,
    },
  },
  /* ── Shared card ── */
  c: {
    background: colors.cardBackground,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 8,
    overflow: "hidden",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: colors.hoverBorder,
      boxShadow: colors.hoverShadow,
    },
  },
  /* ── Metric card ── */
  mc: {
    background: colors.cardBackground,
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 8,
    overflow: "hidden",
    padding: "14px 16px 0",
    display: "flex",
    flexDirection: "column",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: colors.hoverBorderStrong,
      boxShadow: colors.hoverShadowStrong,
    },
  },
  mcTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  mcIcon: { width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  mcLabel: { fontSize: 12, color: colors.textSecondary, fontWeight: 600, marginLeft: 8 },
  mcVal: { fontSize: 28, fontWeight: 700, color: colors.textPrimary, lineHeight: 1, fontVariantNumeric: "tabular-nums", textAlign: "center", width: "100%", margin: "6px 0" },
  mcBadge: { fontSize: 11, fontWeight: 700, padding: "2px 6px", borderRadius: 4, marginLeft: 8 },
  mcLink: { fontSize: 11, color: colors.link, fontWeight: 600, cursor: "pointer", marginTop: 4, "&:hover": { textDecoration: "underline" } },
  mcSpark: { height: 40, width: "calc(100% + 32px)", marginLeft: -16, marginTop: "auto" },
  /* ── Pie strip ── */
  pieStrip: { display: "flex", flexDirection: "column", gap: 0, padding: "8px 10px", height: "100%", justifyContent: "space-evenly" },
  pieRow: { display: "flex", alignItems: "center", gap: 10, padding: "5px 0", borderBottom: `1px solid ${colors.cardBorderSoft}`, "&:last-child": { borderBottom: "none" } },
  pieDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  pieTxt: { fontSize: 12, color: colors.textSecondary, "& strong": { color: colors.textPrimary, fontWeight: 700 } },
  /* ── Section ── */
  sHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px 6px", flexWrap: "wrap", gap: 6 },
  sTitle: { fontSize: 14, fontWeight: 600, color: colors.textPrimary },
  sBody: { padding: "0 8px 6px" },
  /* ── Chips ── */
  fc: { fontSize: 11, fontWeight: 600, height: 26, background: colors.chipBackground, color: colors.chipColor, border: `1px solid ${colors.chipBorder}`, cursor: "pointer" },
  fcOn: { fontSize: 11, fontWeight: 700, height: 26, background: colors.chipBackgroundActive, color: colors.chipColorActive, border: `1px solid ${colors.chipBorderActive}`, cursor: "pointer" },
  ac: { fontSize: 11, fontWeight: 700, height: 24, background: colors.alertBackground, color: colors.alertColor, border: `1px solid ${colors.alertBorder}` },
  fs: {
    minWidth: 124,
    "& .MuiOutlinedInput-root": {
      minHeight: 30,
      borderRadius: 999,
    },
    "& .MuiSelect-select": {
      padding: "6px 28px 6px 10px",
      fontSize: 11,
      fontWeight: 600,
    },
    "& .MuiSelect-icon": {
      fontSize: 16,
    },
  },
  hi: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 14, height: 14, borderRadius: "50%", border: `1px solid ${colors.helperBorder}`, color: colors.textSubtle, fontSize: 8, fontWeight: 700, flexShrink: 0 },
  leg: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 4 },
  legI: { display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: colors.textSecondary },
  /* ── Popover ── */
  rpop: { padding: 10, display: "flex", flexDirection: "column", gap: 8, minWidth: 580, [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100vw", padding: 10 } },
  rpick: {
    "& .rdrCalendarWrapper": { background: colors.calendarBackground, color: colors.calendarText, borderRadius: 8, border: `1px solid ${colors.calendarBorder}`, overflow: "hidden" },
    "& .rdrMonthAndYearWrapper": { paddingTop: 8, paddingBottom: 6, background: colors.calendarHeaderBackground, borderBottom: `1px solid ${colors.calendarHeaderBorder}` },
    "& .rdrMonthAndYearPickers select": { color: colors.calendarText, background: "transparent", fontWeight: 600 },
    "& .rdrWeekDay": { color: colors.calendarWeekday, fontWeight: 600, textTransform: "uppercase", fontSize: "0.68rem" },
    "& .rdrDayNumber span": { color: colors.calendarText },
    "& .rdrDayDisabled": { background: "transparent" },
    "& .rdrDayToday .rdrDayNumber span:after": { background: "#60a5fa" },
    "& .rdrInRange, & .rdrStartEdge, & .rdrEndEdge": { color: "#3b82f6" },
    "& .rdrStartEdge, & .rdrEndEdge": { borderRadius: 6 },
    "& .rdrInRange": { background: colors.calendarRange },
    "& .rdrDefinedRangesWrapper": { display: "none" },
  },
  lgCard: { padding: 6, maxHeight: 180, overflowY: "auto", "&::-webkit-scrollbar": { width: 3 }, "&::-webkit-scrollbar-thumb": { background: colors.scrollbarThumb, borderRadius: 2 } },
  lgTitle: { fontWeight: 600, color: colors.textPrimary, fontSize: 11, marginBottom: 6, textAlign: "center" },
  });
});

/**
 * DashboardGeralBody — corpo reutilizavel do Dashboard Geral, sem
 * MainContainer / titulo / bottomSpacer. Usado por:
 *   - DashboardGeral (default): pagina stand-alone com wrapper
 *   - MultiUnidades aba "Graficos operacionais": embeb dentro do shell da pagina
 *
 * Props:
 *   - embedded (bool, default false): quando true, NAO renderiza o estado
 *     vazio "Grupo nao definido" (deixa quem embeb decidir como tratar) e
 *     tambem pula o wrapper MainContainer/.head com o titulo "Dashboard Geral".
 *   - hideHeader (bool, default false): quando true, NAO renderiza o
 *     titulo "Dashboard Geral — {groupName}" mesmo no modo stand-alone.
 *     Util quando o shell de quem embeb (ex.: MultiUnidades) ja exibe um
 *     titulo de pagina e nao queremos duplicar headers. Quando
 *     embedded=true, hideHeader e tratado como true automaticamente.
 */
export const DashboardGeralBody = ({ embedded = false, hideHeader = false } = {}) => {
  // Em modo embedded o titulo nunca e renderizado (o shell de quem embeb
  // ja exibe o titulo da pagina), portanto hideHeader vira true automatico.
  const shouldHideHeader = hideHeader || embedded;
  const classes = useStyles();
  const theme = useTheme();
  const colors = getDashboardThemeTokens(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const groupName = user?.company?.groupName || "";
  const apiGroup = useMemo(() => makeApiGroup(groupName), [groupName]);
  const [counters, setCounters] = useState({});
  const initialEnd = moment().startOf("hour");
  const initialStart = moment().startOf("hour").subtract(24, "hours");
  const [dateFrom, setDateFrom] = useState(initialStart.format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(initialEnd.format("YYYY-MM-DD"));
  const [last24h, setLast24h] = useState(true);
  const [periodMode, setPeriodMode] = useState("24h");
  const [hourFrom, setHourFrom] = useState(initialStart.format("HH:mm:ss"));
  const [hourTo, setHourTo] = useState(initialEnd.format("HH:mm:ss"));
  const [loading, setLoading] = useState(false);
  const [offendersModalOpen, setOffendersModalOpen] = useState(false);
  const [noResponseHistory, setNoResponseHistory] = useState([]);
  const [showNoResponseChart, setShowNoResponseChart] = useState(false);
  const [autoRefreshKey, setAutoRefreshKey] = useState(0);
  const [noResponse24h, setNoResponse24h] = useState({ count: 0, rate: 0 });
  const [counters24h, setCounters24h] = useState({ activeConversations: 0, avgPerAttendant: 0, pending: 0 });
  const [rangeAnchorEl, setRangeAnchorEl] = useState(null);
  const [draftRange, setDraftRange] = useState({ startDate: initialStart.toDate(), endDate: initialEnd.toDate(), key: "selection" });
  const [noResponseUsers, setNoResponseUsers] = useState([]);
  const [noResponseUserId, setNoResponseUserId] = useState("all");
  const [aiHumanPie, setAiHumanPie] = useState({ ai: 0, human: 0, total: 0 });
  const [onlineOfflinePie, setOnlineOfflinePie] = useState({ online: 0, offline: 0, total: 0 });
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersWithStatus, setUsersWithStatus] = useState([]);
  // ART (Average Response Time) — card (rolling 24h) + analytics (período selecionado)
  const [art24h, setArt24h] = useState({ avgMinutes: null, pairCount: 0, status: "empty", scheduleConfigured: false, history: [], thresholds: null });
  const [artPeriod, setArtPeriod] = useState({ avgMinutes: null, pairCount: 0, status: "empty", granularity: "day", history: [], ranking: [], scheduleConfigured: false, thresholds: null });

  useEffect(() => { if (!groupName) return; fetchPieCharts(); }, [autoRefreshKey, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  async function fetchPieCharts() {
    try {
      const [r2, r3] = await Promise.all([apiGroup.get("/dashboard/aiVsHumanPie"), apiGroup.get("/dashboard/onlineVsOfflinePie")]);
      setAiHumanPie(r2.data); setOnlineOfflinePie(r3.data);
    } catch (err) { console.error(err); }
  }

  // Refresh em tempo real: quando qualquer ticket muda (atribuição IA/humano,
  // resolução, etc), o backend emite `company-${companyId}-ticket`. Aqui
  // disparamos um refetch com debounce de 2s para evitar storm quando muitos
  // tickets mudam em sequência.
  const socketManager = useContext(SocketContext);
  const ticketRefreshDebounceRef = useRef(null);
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) return undefined;
    const socket = socketManager.getSocket(companyId);
    const handleTicketEvent = (data) => {
      if (!data || (data.action !== "update" && data.action !== "create" && data.action !== "delete")) return;
      if (ticketRefreshDebounceRef.current) clearTimeout(ticketRefreshDebounceRef.current);
      ticketRefreshDebounceRef.current = setTimeout(() => {
        setAutoRefreshKey((k) => k + 1);
      }, 2000);
    };
    socket.on(`company-${companyId}-ticket`, handleTicketEvent);

    // Presença em tempo real: atualiza onlineOfflinePie imediatamente, sem refetch.
    // Backend emite "user-presence" para `company-${companyId}-mainchannel` quando
    // o primeiro socket do usuário sobe ou o último cai.
    const seenUsers = new Map(); // userId -> last-seen-online-bool, dedup contra eventos duplicados
    const handlePresence = (data) => {
      if (!data || typeof data.userId !== "number") return;
      const prev = seenUsers.get(data.userId);
      if (prev === data.online) return; // sem mudança real
      seenUsers.set(data.userId, data.online);
      setOnlineOfflinePie(p => {
        const delta = data.online ? 1 : -1;
        const online = Math.max(0, p.online + delta);
        const offline = Math.max(0, p.offline - delta);
        return { ...p, online, offline };
      });
    };
    socket.on("user-presence", handlePresence);

    return () => {
      socket.off(`company-${companyId}-ticket`, handleTicketEvent);
      socket.off("user-presence", handlePresence);
      if (ticketRefreshDebounceRef.current) clearTimeout(ticketRefreshDebounceRef.current);
    };
  }, [socketManager]);

  useEffect(() => { if (!groupName) return; fetchData(); }, [dateFrom, dateTo, autoRefreshKey, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!groupName) return; fetchNoResponseHistory(); }, [dateFrom, dateTo, autoRefreshKey, noResponseUserId, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!groupName) return; fetchNoResponse24h(); }, [autoRefreshKey, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!groupName) return; fetchART24h(); }, [autoRefreshKey, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!groupName) return; fetchARTPeriod(); }, [dateFrom, dateTo, hourFrom, hourTo, last24h, autoRefreshKey, noResponseUserId, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { try { const { data } = await api.get("/users/list"); setNoResponseUsers(data || []); } catch(e) { setNoResponseUsers([]); } })(); }, []);

  async function fetchData() {
    setLoading(true);
    const tz = moment().format("Z");
    const params = last24h ? { rolling_24h: true, tz } : { date_from: moment(dateFrom).format("YYYY-MM-DD"), date_to: moment(dateTo).format("YYYY-MM-DD"), noresponse_date_from: moment(dateFrom).format("YYYY-MM-DD"), noresponse_date_to: moment(dateTo).format("YYYY-MM-DD"), tz };
    try { const { data } = await apiGroup.get("/dashboard", { params }); setCounters(data.counters); } catch (e) { toast.error(i18n.t("dashboard.toasts.selectFilterError")); }
    setLoading(false);
  }
  async function fetchNoResponse24h() {
    try {
      const { data } = await apiGroup.get("/dashboard", { params: { rolling_24h: true, tz: moment().format("Z") } });
      setNoResponse24h({ count: data?.counters?.noResponse || 0, rate: data?.counters?.noResponseRate || 0 });
      setCounters24h({ activeConversations: data?.counters?.activeConversations || 0, avgPerAttendant: data?.counters?.avgPerAttendant || 0, pending: data?.counters?.pending || 0 });
    } catch (e) { setNoResponse24h({ count: 0, rate: 0 }); setCounters24h({ activeConversations: 0, avgPerAttendant: 0, pending: 0 }); }
  }
  const openUsersModal = async () => {
    setUsersModalOpen(true);
    try {
      const { data } = await api.get("/internal-chat/users");
      setUsersWithStatus(data || []);
    } catch { setUsersWithStatus([]); }
  };
  async function fetchNoResponseHistory() {
    if (!dateFrom || !dateTo) { setShowNoResponseChart(false); return; }
    try {
      const { data } = await apiGroup.get("/dashboard/noResponseHistory", { params: { date_from: dateFrom, date_to: dateTo, tz: moment().format("Z"), ...(last24h ? { hour_from: hourFrom, hour_to: hourTo } : {}), ...(noResponseUserId !== "all" ? { user_id: noResponseUserId } : {}) } });
      setNoResponseHistory(data); setShowNoResponseChart(true);
    } catch (e) { setShowNoResponseChart(false); }
  }
  async function fetchART24h() {
    try {
      const { data } = await apiGroup.get("/dashboard/art", { params: { rolling_24h: true, tz: moment().format("Z") } });
      setArt24h({
        avgMinutes: data?.avgMinutes ?? null,
        pairCount: data?.pairCount || 0,
        status: data?.status || "empty",
        scheduleConfigured: !!data?.scheduleConfigured,
        history: data?.history || [],
        thresholds: data?.thresholds || null,
      });
    } catch (e) {
      setArt24h({ avgMinutes: null, pairCount: 0, status: "empty", scheduleConfigured: false, history: [], thresholds: null });
    }
  }
  async function fetchARTPeriod() {
    if (!dateFrom || !dateTo) return;
    try {
      const params = {
        tz: moment().format("Z"),
        ...(last24h
          ? { rolling_24h: true }
          : { date_from: dateFrom, date_to: dateTo, hour_from: hourFrom, hour_to: hourTo }),
        ...(noResponseUserId !== "all" ? { user_id: noResponseUserId } : {}),
      };
      const { data } = await apiGroup.get("/dashboard/art", { params });
      setArtPeriod({
        avgMinutes: data?.avgMinutes ?? null,
        pairCount: data?.pairCount || 0,
        status: data?.status || "empty",
        granularity: data?.granularity || "day",
        history: data?.history || [],
        ranking: data?.ranking || [],
        scheduleConfigured: !!data?.scheduleConfigured,
        thresholds: data?.thresholds || null,
      });
    } catch (e) {
      setArtPeriod({ avgMinutes: null, pairCount: 0, status: "empty", granularity: "day", history: [], ranking: [], scheduleConfigured: false, thresholds: null });
    }
  }

  const fmt = (s, e) => { if (last24h) return "Últimas 24h"; if (!s || !e) return "Período"; const a = moment(s).locale("pt-br"), b = moment(e).locale("pt-br"); if (a.isSame(b, "day")) return a.format("D MMM"); return `${a.format("D MMM")} – ${b.format("D MMM")}`; };
  const getRangeLabel = () => fmt(dateFrom, dateTo);
  const getDraftRangeLabel = () => fmt(draftRange.startDate, draftRange.endDate);
  const isHourly = last24h || dateFrom === dateTo;

  const chartData = useMemo(() => {
    const items = (noResponseHistory || []).map(item => ({ ...item, xKey: isHourly ? moment(item.date).format("YYYY-MM-DD HH:00:00") : moment(item.date).format("YYYY-MM-DD"), rawDate: item.date }));
    if (!isHourly) return items;
    if (!last24h && dateFrom !== moment().format("YYYY-MM-DD")) return items;
    const cur = moment().startOf("hour");
    return items.filter(item => moment(item.rawDate).isSameOrBefore(cur));
  }, [noResponseHistory, isHourly, dateFrom, dateTo, last24h]);

  const avgRate = useMemo(() => { const t = (chartData || []).reduce((a, i) => { a.n += Number(i.noResponseCount || 0); a.a += Number(i.activeCount || 0); return a; }, { n: 0, a: 0 }); return t.a === 0 ? 0 : Math.round((t.n / t.a) * 100); }, [chartData]);

  const openRange = (e) => {
    setDraftRange(last24h ? { startDate: moment(`${dateFrom}T${hourFrom}`).toDate(), endDate: moment(`${dateTo}T${hourTo}`).toDate(), key: "selection" } : { startDate: moment(dateFrom).toDate(), endDate: moment(dateTo).toDate(), key: "selection" });
    setRangeAnchorEl(e.currentTarget);
  };
  const applyRange = () => {
    if (!draftRange.startDate || !draftRange.endDate) { toast.error("Selecione datas."); return; }
    if (last24h) { sel24h(); setRangeAnchorEl(null); return; }
    setDateFrom(moment(draftRange.startDate).format("YYYY-MM-DD")); setDateTo(moment(draftRange.endDate).format("YYYY-MM-DD"));
    setHourFrom("00:00:00"); setHourTo("23:59:59"); setPeriodMode("custom"); setAutoRefreshKey(k => k + 1); setRangeAnchorEl(null);
  };
  const sel24h = () => {
    const e = moment().startOf("hour"), s = moment().subtract(24, "hours").startOf("hour");
    setDateFrom(s.format("YYYY-MM-DD")); setDateTo(e.format("YYYY-MM-DD")); setHourFrom(s.format("HH:mm:ss")); setHourTo(e.format("HH:mm:ss"));
    setLast24h(true); setPeriodMode("24h"); setDraftRange({ startDate: s.toDate(), endDate: e.toDate(), key: "selection" }); setAutoRefreshKey(k => k + 1);
  };
  const selDays = (n) => {
    const e = moment().format("YYYY-MM-DD"), s = moment().subtract(n, "days").format("YYYY-MM-DD");
    setDateFrom(s); setDateTo(e); setHourFrom("00:00:00"); setHourTo("23:59:59");
    setLast24h(false); setPeriodMode(n === 7 ? "7d" : "30d");
    setDraftRange({ startDate: moment(s).toDate(), endDate: moment(e).toDate(), key: "selection" }); setAutoRefreshKey(k => k + 1);
  };

  // Sparkline data — sempre baseado nos dados 24h, independente do filtro de período
  const [sparkData, setSparkData] = useState([]);
  useEffect(() => {
    if (!groupName) return;
    (async () => {
      try {
        const tz = moment().format("Z");
        const s = moment().subtract(24, "hours").startOf("hour").format("YYYY-MM-DD");
        const e = moment().startOf("hour").format("YYYY-MM-DD");
        const hf = moment().subtract(24, "hours").startOf("hour").format("HH:mm:ss");
        const ht = moment().startOf("hour").format("HH:mm:ss");
        const { data } = await apiGroup.get("/dashboard/noResponseHistory", { params: { date_from: s, date_to: e, hour_from: hf, hour_to: ht, tz } });
        setSparkData(data || []);
      } catch { setSparkData([]); }
    })();
  }, [autoRefreshKey, groupName]); // eslint-disable-line react-hooks/exhaustive-deps
  const flatLine = (val) => Array.from({ length: 12 }, () => ({ v: Math.max(1, val || 1) }));
  const sp1 = useMemo(() => sparkData.length >= 2 ? sparkData.slice(-12).map(d => ({ v: Number(d.activeCount || 0) })) : flatLine(counters24h.activeConversations), [sparkData, counters24h.activeConversations]);
  const sp2 = useMemo(() => sparkData.length >= 2 ? sp1.map(d => ({ v: Math.max(1, Math.round(d.v / Math.max(1, noResponseUsers.length || 3))) })) : flatLine(counters24h.avgPerAttendant), [sp1, sparkData, noResponseUsers.length, counters24h.avgPerAttendant]);
  const sp3 = useMemo(() => sparkData.length >= 2 ? sparkData.slice(-12).map(d => ({ v: Math.max(0, Number(d.activeCount || 0) - Number(d.noResponseCount || 0)) })) : flatLine(counters24h.pending), [sparkData, counters24h.pending]);
  const sp4 = useMemo(() => sparkData.length >= 2 ? sparkData.slice(-12).map(d => ({ v: Number(d.noResponseCount || 0) })) : flatLine(noResponse24h.count), [sparkData, noResponse24h.count]);

  // Cores e helpers do ART
  const artColorFor = (status, min, thresholds) => {
    if (status === "green") return "#22c55e";
    if (status === "yellow") return "#f59e0b";
    if (status === "red") return "#ef4444";
    if (typeof min === "number") {
      const yellowMin = thresholds?.yellowMin ?? 10;
      const redMin = thresholds?.redMin ?? 20;
      if (min <= yellowMin) return "#22c55e";
      if (min <= redMin) return "#f59e0b";
      return "#ef4444";
    }
    return "#64748b";
  };
  const formatARTMinutes = (min) => {
    if (min === null || min === undefined || Number.isNaN(min)) return "—";
    if (min < 1) return `${Math.round(min * 60)}s`;
    if (min < 60) return `${Math.round(min * 10) / 10} min`;
    const h = Math.floor(min / 60);
    const m = Math.round(min - h * 60);
    return `${h}h${m > 0 ? ` ${m}min` : ""}`;
  };
  const artColor = artColorFor(art24h.status, art24h.avgMinutes, art24h.thresholds);
  const artPeriodColor = artColorFor(artPeriod.status, artPeriod.avgMinutes, artPeriod.thresholds);

  // Sparkline do card ART — sempre baseada no rolling 24h, independente
  // do filtro de período (mesmo padrão dos demais cards superiores).
  const sp5 = useMemo(() => {
    const series = (art24h?.history || []).filter(p => p && typeof p.avgMinutes === "number");
    if (series.length >= 2) return series.slice(-12).map(p => ({ v: Number(p.avgMinutes || 0) }));
    return flatLine(art24h.avgMinutes || 0);
  }, [art24h]);

  const mets = [
    {
      area: "m1", icon: MessageCircle, val: counters24h.activeConversations, label: "Atendimentos", color: "#3b82f6", spark: sp1,
      tooltip: "Conversas ativas (open + pending) nas últimas 24h. Conta apenas tickets individuais (sem grupos)."
    },
    {
      area: "m2", icon: TrendingUpIcon, val: counters24h.avgPerAttendant, label: "Média/Atendente", color: "#22c55e", spark: sp2,
      tooltip: "Média de atendimentos por atendente humano nas últimas 24h. Divide o total de tickets atribuídos pelo nº de atendentes que receberam ao menos um. Não considera usuários IA."
    },
    {
      area: "m3", icon: HourglassEmptyIcon, val: counters24h.pending, label: "Pendentes de Aceite", color: "#f59e0b", spark: sp3,
      periodLabel: "7d",
      tooltip: "Tickets criados nos últimos 7 dias que ainda estão aguardando um atendente aceitar (status = pending). Tickets antigos sem aceite não são contados. Atualiza em tempo real conforme tickets entram ou são aceitos."
    },
    {
      area: "m4", icon: HourglassEmptyIcon, val: noResponse24h.count, label: "Sem Resposta +10min", color: "#ef4444", spark: sp4,
      badge: noResponse24h.rate > 0 ? `${noResponse24h.rate}%` : null, click: () => setOffendersModalOpen(true), link: "ver todos",
      tooltip: "Tickets abertos cuja última mensagem do cliente está há mais de 10 minutos sem resposta do atendente. Clique para ver detalhes."
    },
    {
      area: "m5",
      icon: TimerIcon,
      isCustom: true,
      label: "Average Response Time (ART)",
      color: artColor,
      spark: sp5,
      valDisplay: formatARTMinutes(art24h.avgMinutes),
      badge: art24h.status !== "empty" ? (
        art24h.status === "green" ? "BOM" : art24h.status === "yellow" ? "ATENÇÃO" : "CRÍTICO"
      ) : null,
      tooltip: `Average Response Time (ART) — tempo médio que os atendentes (humanos e IA) levam para responder o cliente. Cada mensagem enviada pelo cliente que recebe uma resposta entra na média (não apenas a primeira da conversa). Considera apenas o horário operacional configurado pela empresa em Configurações > Horários (respeita feriados, dias fechados e datas especiais). Verde até ${art24h.thresholds?.yellowMin ?? 10}min, amarelo até ${art24h.thresholds?.redMin ?? 20}min, vermelho acima disso.`,
    },
  ];

  const pies = [
    {
      title: "Mensagens enviadas: IA x Humano",
      tooltip: "Mensagens enviadas (fromMe=true) nas últimas 24h, classificadas pelo dono do ticket: IA (AiAgent ou usuário com isAI=true) vs atendente humano. Não inclui mensagens recebidas do cliente, mensagens de grupo, deletadas ou sem atribuição. Percentual é sobre o total enviado.",
      data: [{ v: aiHumanPie.ai }, { v: aiHumanPie.human }],
      colors: ["#8b5cf6", "#3b82f6"],
      rows: [
        { v: aiHumanPie.ai, l: `IA (${aiHumanPie.total ? Math.round((aiHumanPie.ai / aiHumanPie.total) * 100) : 0}%)` },
        { v: aiHumanPie.human, l: `Humano (${aiHumanPie.total ? Math.round((aiHumanPie.human / aiHumanPie.total) * 100) : 0}%)` }
      ]
    },
    {
      title: "Usuários Online",
      tooltip: "Atendentes humanos da empresa atualmente conectados via socket. Atualiza em tempo real: conta como online a partir do primeiro socket aberto (múltiplas abas contam como um) e cai a offline quando o último socket fecha. Usuários IA não são contados.",
      data: [{ v: onlineOfflinePie.online }, { v: onlineOfflinePie.offline }],
      colors: ["#22c55e", "#64748b"],
      rows: [{ v: onlineOfflinePie.online, l: "Online" }, { v: onlineOfflinePie.offline, l: "Offline" }],
      click: openUsersModal, link: "ver detalhes"
    },
  ];

  const ttip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (<div style={{ background: colors.tooltipBackground, borderRadius: 6, padding: "8px 12px", border: `1px solid ${colors.tooltipBorder}` }}>
      <div style={{ color: "#fff", fontWeight: 600, fontSize: 10, marginBottom: 4 }}>{isHourly ? moment(d?.rawDate || label).format("DD/MM HH:mm") : moment(d?.rawDate || label).format("DD/MM/YYYY")}</div>
      <div style={{ color: "#D0021B", fontSize: 10 }}>Atraso: <strong>{d?.noResponseCount || 0}</strong></div>
      <div style={{ color: "#7ED321", fontSize: 10 }}>Ativos: <strong>{d?.activeCount || 0}</strong></div>
      <div style={{ color: "#4A90E2", fontSize: 10 }}>Taxa: <strong>{d?.percentage || 0}%</strong></div>
    </div>);
  };

  if (!groupName) {
    if (embedded) {
      return (
        <div className={classes.emptyState}>
          Grupo não definido para esta empresa.
        </div>
      );
    }
    return (
      <MainContainer className={classes.page}>
        <div className={classes.root}>
          {!shouldHideHeader && (
            <div className={classes.head}>
              <span className={classes.title}>Dashboard Geral</span>
            </div>
          )}
          <div className={classes.emptyState}>
            Grupo não definido para esta empresa.
          </div>
        </div>
      </MainContainer>
    );
  }

  const body = (
    <>
        <Popover open={Boolean(rangeAnchorEl)} anchorEl={rangeAnchorEl} onClose={() => setRangeAnchorEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
          <Box className={classes.rpop}>
            <div className={classes.rpick}>
              <DateRangePicker ranges={[draftRange]} onChange={item => { setDraftRange(item.selection); setLast24h(false); }} moveRangeOnFirstSelection={false} showSelectionPreview months={isMobile ? 1 : 2} direction={isMobile ? "vertical" : "horizontal"} locale={ptBR} rangeColors={["#3b82f6"]} />
            </div>
            <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Chip label={getDraftRangeLabel()} className={classes.fc} size="small" />
              <Box style={{ display: "flex", gap: 6 }}>
                <Button size="small" onClick={() => setRangeAnchorEl(null)}>Cancelar</Button>
                <Button size="small" color="primary" variant="contained" onClick={applyRange}>Aplicar</Button>
              </Box>
            </Box>
          </Box>
        </Popover>

        <NoResponseOffendersModal open={offendersModalOpen} onClose={() => setOffendersModalOpen(false)} forceLast24h={false} groupName={groupName} />

        {/* ══════ MASTER GRID ══════ */}
        <div className={classes.master}>

          {/* ── 4 Metric Cards ── */}
          {mets.map((m, i) => (
            <div key={i} className={classes.mc} style={{ gridArea: m.area, cursor: m.click ? "pointer" : "default" }} onClick={m.click}>
              <div className={classes.mcTop}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div className={classes.mcIcon} style={{ background: `${m.color}18` }}>
                    <m.icon style={{ fontSize: 12, color: m.color }} />
                  </div>
                  <span className={classes.mcLabel}>{m.label}</span>
                  {m.tooltip && (
                    <Tooltip title={m.tooltip} arrow placement="top" interactive>
                      <span className={classes.hi} onClick={e => e.stopPropagation()}>?</span>
                    </Tooltip>
                  )}
                </div>
                <span style={{ fontSize: 8, color: colors.textSubtle, fontWeight: 500 }}>{m.periodLabel || "24h"}</span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span className={classes.mcVal}>{m.isCustom ? (m.valDisplay || "—") : (m.val || 0)}</span>
                {m.badge && <span className={classes.mcBadge} style={{ background: `${m.color}18`, color: m.color }}>{m.badge}</span>}
              </div>
              {m.link && <span className={classes.mcLink}>{m.link} →</span>}
              <div className={classes.mcSpark}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={m.spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                    <defs><linearGradient id={`s${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={m.color} stopOpacity={0.35} /><stop offset="100%" stopColor={m.color} stopOpacity={0.02} /></linearGradient></defs>
                    <Area type="monotone" dataKey="v" stroke={m.color} strokeWidth={1.5} fill={`url(#s${i})`} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}

          {/* ── Heatmap ── */}
          <div className={classes.c} style={{ gridArea: "heat", padding: "6px 8px" }}>
            <ChartsHeatmap groupName={groupName} />
          </div>

          {/* ── 3 Pie Charts ── */}
          <div className={classes.c} style={{ gridArea: "pie", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 10px 0", display: "flex", alignItems: "center", gap: 4 }}>
              <span className={classes.sTitle}>Visão Geral</span>
            </div>
            <div className={classes.pieStrip}>
              {pies.map((p, pi) => {
                const total = p.data.reduce((s, d) => s + d.v, 0);
                return (
                  <div key={pi} className={classes.pieRow} style={p.click ? { cursor: "pointer", borderRadius: 6, transition: "background 0.15s ease" } : undefined} onClick={p.click} onMouseEnter={p.click ? e => e.currentTarget.style.background = colors.rowHover : undefined} onMouseLeave={p.click ? e => e.currentTarget.style.background = "transparent" : undefined}>
                    <div style={{ width: 48, height: 48, flexShrink: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={total > 0 ? p.data : [{ v: 1 }]} cx="50%" cy="50%" innerRadius={14} outerRadius={22} paddingAngle={total > 0 ? 3 : 0} dataKey="v" strokeWidth={0}>
                            {total > 0 ? p.colors.map((c, ci) => <Cell key={ci} fill={c} />) : <Cell fill={colors.emptyPie} />}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: colors.textPrimary }}>{p.title}</span>
                          {p.tooltip && (
                            <Tooltip title={p.tooltip} arrow placement="top" interactive>
                              <span className={classes.hi} onClick={e => e.stopPropagation()}>?</span>
                            </Tooltip>
                          )}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: "tabular-nums", opacity: 0.6 }}>{total}</span>
                      </div>
                      {p.rows.map((r, ri) => (
                        <div key={ri} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
                          <div className={classes.pieDot} style={{ background: p.colors[ri] }} />
                          <span className={classes.pieTxt}><strong>{r.v}</strong> {r.l}</span>
                        </div>
                      ))}
                      {p.link && <span style={{ fontSize: 9, color: colors.link, fontWeight: 600, marginTop: 2, display: "inline-block" }}>{p.link} →</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Filtro de Período (afeta apenas Histórico e Mensagens) ── */}
          <div style={{ gridArea: "filt", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 0" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted }}>Análise por período</span>
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {[{ l: "24h", m: "24h", fn: sel24h }, { l: "7d", m: "7d", fn: () => selDays(7) }, { l: "30d", m: "30d", fn: () => selDays(30) }].map(p => (
                <Chip key={p.m} label={p.l} onClick={p.fn} className={periodMode === p.m ? classes.fcOn : classes.fc} size="small" />
              ))}
              <Chip label={periodMode === "custom" ? getRangeLabel() : "Personalizado"} onClick={openRange} className={periodMode === "custom" ? classes.fcOn : classes.fc} size="small" />
            </div>
          </div>

          {/* ── Histórico Sem Resposta ── */}
          <div className={classes.c} style={{ gridArea: "hist", display: "flex", flexDirection: "column" }}>
            <div className={classes.sHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.sTitle}>Histórico — Sem Resposta +10min</span>
                <Tooltip
                  title="Série temporal de tickets sem resposta >10min vs tickets ativos no período. A linha azul é a taxa (%). Respeita o filtro de período (24h/7d/30d/personalizado) e o seletor de atendente. Se 'horário comercial' estiver configurado, considera apenas horas dentro do expediente."
                  arrow placement="top" interactive
                >
                  <span className={classes.hi}>?</span>
                </Tooltip>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                <ModernSelect
                  value={noResponseUserId}
                  onChange={e => setNoResponseUserId(e.target.value)}
                  className={classes.fs}
                  formControlProps={{ size: "small" }}
                >
                    <MenuItem value="all">Todos</MenuItem>
                    {noResponseUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                </ModernSelect>
                <Chip label={`Média ${avgRate}%`} className={classes.ac} size="small" />
              </div>
            </div>
            <div style={{ padding: "0 6px 2px", flex: 1 }}>
              <div className={classes.leg}>
                <span className={classes.legI}><Circle size={7} style={{ color: "#D0021B" }} /> Sem Resposta</span>
                <span className={classes.legI}><Circle size={7} style={{ color: "#7ED321" }} /> Ativos</span>
                <span className={classes.legI}><Circle size={7} style={{ color: "#4A90E2" }} /> Taxa %</span>
              </div>
              <ResponsiveContainer width="100%" height={isMobile ? 140 : 160}>
                <AreaChart data={chartData.length > 0 ? chartData : [{ xKey: "", noResponseCount: 0, activeCount: 0, percentage: 0 }]} margin={{ top: 4, right: 16, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gNR" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#D0021B" stopOpacity={0.25} /><stop offset="95%" stopColor="#D0021B" stopOpacity={0.02} /></linearGradient>
                    <linearGradient id="gAC" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7ED321" stopOpacity={0.25} /><stop offset="95%" stopColor="#7ED321" stopOpacity={0.02} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                  <XAxis dataKey="xKey" tickFormatter={v => isHourly ? moment(v).format("HH[h]") : moment(v).format("DD/MM")} tick={{ fontSize: 9, fill: colors.textSubtle }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 9, fill: colors.textSubtle }} axisLine={false} tickLine={false} label={{ value: "Qtd", angle: -90, position: "insideLeft", style: { fontSize: 9, fill: colors.textSubtle } }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 9, fill: colors.textSubtle }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                  <RTooltip content={ttip} />
                  <Area yAxisId="left" type="monotone" dataKey="activeCount" stroke="#7ED321" strokeWidth={1.5} fill="url(#gAC)" dot={false} />
                  <Area yAxisId="left" type="monotone" dataKey="noResponseCount" stroke="#D0021B" strokeWidth={1.5} fill="url(#gNR)" dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="percentage" stroke="#4A90E2" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Mensagens por Usuário ── */}
          <div className={classes.c} style={{ gridArea: "msgs", display: "flex", flexDirection: "column" }}>
            <div className={classes.sHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.sTitle}>Mensagens por Usuário</span>
                <Tooltip
                  title="Mensagens enviadas (fromMe=true) por cada atendente humano e agente IA dentro do período selecionado. Não inclui mensagens recebidas do cliente, deletadas, de grupos nem tickets sem atribuição."
                  arrow placement="top" interactive
                >
                  <span className={classes.hi}>?</span>
                </Tooltip>
              </div>
            </div>
            <div style={{ padding: "0 6px 2px", flex: 1, display: "flex", gap: 6 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ChartsMessagesPerUser initialDate={dateFrom} finalDate={dateTo} last24h={last24h} hourFrom={hourFrom} hourTo={hourTo} groupName={groupName} />
              </div>
              <div className={classes.lgCard}>
                <div className={classes.lgTitle}>Usuários</div>
                <div id="chart-legend"></div>
              </div>
            </div>
          </div>

          {/* ── Histórico ART ── */}
          <div className={classes.c} style={{ gridArea: "arthist", display: "flex", flexDirection: "column" }}>
            <div className={classes.sHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.sTitle}>Histórico — Average Response Time</span>
                <Tooltip title="Average Response Time (ART) — tempo médio de resposta dos atendentes a cada mensagem do cliente (não apenas à primeira da conversa), considerando apenas o horário operacional configurado pela empresa (feriados e dias fechados são ignorados)." arrow placement="top" interactive>
                  <span className={classes.hi}>?</span>
                </Tooltip>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                {!artPeriod.scheduleConfigured && (
                  <Chip label="Horário não configurado" className={classes.ac} size="small" />
                )}
                <Chip
                  label={artPeriod.avgMinutes !== null ? `Média ${formatARTMinutes(artPeriod.avgMinutes)}` : "Sem dados"}
                  className={classes.fc}
                  size="small"
                  style={{ background: `${artPeriodColor}18`, color: artPeriodColor, borderColor: `${artPeriodColor}40` }}
                />
              </div>
            </div>
            <div style={{ padding: "0 6px 2px", flex: 1 }}>
              <div className={classes.leg}>
                <span className={classes.legI}><Circle size={7} style={{ color: artPeriodColor }} /> ART (min, dentro do expediente)</span>
              </div>
              <ResponsiveContainer width="100%" height={isMobile ? 140 : 160}>
                <AreaChart
                  data={(artPeriod.history && artPeriod.history.length > 0) ? artPeriod.history.map(p => ({ ...p, xKey: p.bucket })) : [{ xKey: "", avgMinutes: null, pairCount: 0 }]}
                  margin={{ top: 4, right: 16, left: -12, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="gART" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={artPeriodColor} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={artPeriodColor} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                  <XAxis
                    dataKey="xKey"
                    tickFormatter={v => v ? (artPeriod.granularity === "hour" ? moment(v).format("DD/MM HH[h]") : moment(v).format("DD/MM")) : ""}
                    tick={{ fontSize: 9, fill: colors.textSubtle }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis tick={{ fontSize: 9, fill: colors.textSubtle }} axisLine={false} tickLine={false} tickFormatter={v => `${v}m`} />
                  <RTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload || {};
                      return (
                        <div style={{ background: colors.tooltipBackground, borderRadius: 6, padding: "8px 12px", border: `1px solid ${colors.tooltipBorder}` }}>
                          <div style={{ color: "#fff", fontWeight: 600, fontSize: 10, marginBottom: 4 }}>
                            {d.bucket ? (artPeriod.granularity === "hour" ? moment(d.bucket).format("DD/MM HH:mm") : moment(d.bucket).format("DD/MM/YYYY")) : ""}
                          </div>
                          <div style={{ color: artPeriodColor, fontSize: 10 }}>ART: <strong>{d.avgMinutes !== null && d.avgMinutes !== undefined ? formatARTMinutes(d.avgMinutes) : "—"}</strong></div>
                          <div style={{ color: colors.textMuted, fontSize: 10 }}>Atendimentos: <strong>{d.pairCount || 0}</strong></div>
                        </div>
                      );
                    }}
                  />
                  <Area type="monotone" dataKey="avgMinutes" stroke={artPeriodColor} strokeWidth={2} fill="url(#gART)" dot={false} connectNulls />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Ranking ART por Atendente ── */}
          <div className={classes.c} style={{ gridArea: "artrank", display: "flex", flexDirection: "column" }}>
            <div className={classes.sHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className={classes.sTitle}>Ranking ART por Atendente</span>
                <Tooltip title="Ordenado do menor ART (melhor) para o maior. Inclui agentes IA. Respeita todos os filtros do painel." arrow placement="top" interactive>
                  <span className={classes.hi}>?</span>
                </Tooltip>
              </div>
            </div>
            <div style={{ padding: "0 10px 10px", flex: 1, overflowY: "auto", maxHeight: 280 }}>
              {artPeriod.ranking && artPeriod.ranking.length > 0 ? (
                <div>
                  <div style={{ display: "grid", gridTemplateColumns: "24px 1fr auto auto", gap: 8, alignItems: "center", padding: "4px 6px", borderBottom: `1px solid ${colors.cardBorderSoft}`, fontSize: 10, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    <span>#</span>
                    <span>Atendente</span>
                    <span style={{ textAlign: "right" }}>ART</span>
                    <span style={{ textAlign: "right" }}>Qtd</span>
                  </div>
                  {artPeriod.ranking.map((row, i) => {
                    const rowColor = artColorFor(null, row.avgMinutes);
                    const breakdown = Array.isArray(row.breakdown) ? row.breakdown : [];
                    const hasBreakdown = breakdown.length > 1;
                    const tooltipTitle = hasBreakdown
                      ? `Média ponderada por pares:\n${breakdown
                          .map(b => `${b.companyName}: ${formatARTMinutes(b.avgMinutes)} × ${b.pairCount} pares`)
                          .join("\n")}\nTotal: ${row.pairCount} pares → ${formatARTMinutes(row.avgMinutes)}`
                      : "";
                    // NameCell precisa ser display:block + width:100% pra hit-area
                    // do hover cobrir a célula inteira. Sem isso, span inline = hit
                    // area só na largura do texto e o tooltip flicker em
                    // qualquer micro-movimento horizontal.
                    const NameCell = (
                      <span style={{ display: "block", width: "100%", fontSize: 12, color: colors.textPrimary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: hasBreakdown ? "help" : "default" }}>
                        {row.name}{row.isAI && !/IA\)$/.test(row.name) ? " (IA)" : ""}
                      </span>
                    );
                    return (
                      <div key={`${row.name}-${row.isAI ? "ai" : "h"}-${i}`} style={{ display: "grid", gridTemplateColumns: "24px 1fr auto auto", gap: 8, alignItems: "center", padding: "6px", borderBottom: `1px solid ${colors.cardBorderSoft}` }}>
                        <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 700 }}>{i + 1}</span>
                        {hasBreakdown ? (
                          <Tooltip
                            title={<span style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</span>}
                            arrow
                            placement="top"
                          >
                            {NameCell}
                          </Tooltip>
                        ) : NameCell}
                        <span style={{ fontSize: 12, fontWeight: 700, color: rowColor, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{formatARTMinutes(row.avgMinutes)}</span>
                        <span style={{ fontSize: 11, color: colors.textMuted, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{row.pairCount}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: 18, textAlign: "center", fontSize: 12, color: colors.textMuted }}>
                  {artPeriod.scheduleConfigured ? "Sem dados no período." : "Configure os horários de atendimento para habilitar o ART."}
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── Volume de mensagens — escopado à empresa, full-width ── */}
        <MessageStats
          endpoint="/dashboard/messages-stats"
          refreshEndpoint="/dashboard/messages-stats/refresh"
          connectionsEndpoint="/dashboard/connections"
          hideCompanyFilter
          hideCompanyColumn
          subtitle="Não oficial + API Oficial · agregado de todas as conexões do grupo · clique em atualizar para refresh manual"
          groupName={groupName}
        />
        <div className={classes.bottomSpacer} />

        {/* ── Modal Usuários Online/Offline ── */}
        <Dialog open={usersModalOpen} onClose={() => setUsersModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ style: { background: colors.cardBackgroundStrong, border: `1px solid ${colors.cardBorder}`, borderRadius: 10, boxShadow: colors.dialogShadow } }}>
          <DialogTitle disableTypography style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>Usuários ({usersWithStatus.length})</span>
            <IconButton size="small" onClick={() => setUsersModalOpen(false)} style={{ color: colors.textMuted }}><X size={18} /></IconButton>
          </DialogTitle>
          <DialogContent style={{ padding: "0 16px 16px", maxHeight: 400 }}>
            {usersWithStatus.filter(u => u.online).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#22c55e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Online ({usersWithStatus.filter(u => u.online).length})</div>
                {usersWithStatus.filter(u => u.online).map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${colors.dialogDivider}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: colors.textPrimary, flex: 1 }}>{u.name}</span>
                    <span style={{ fontSize: 10, color: colors.textMuted }}>{u.profile}</span>
                  </div>
                ))}
              </div>
            )}
            {usersWithStatus.filter(u => !u.online).length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Offline ({usersWithStatus.filter(u => !u.online).length})</div>
                {usersWithStatus.filter(u => !u.online).map(u => (
                  <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${colors.dialogDivider}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#64748b", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: colors.textSecondary, flex: 1 }}>{u.name}</span>
                    <span style={{ fontSize: 10, color: colors.textSubtle }}>{u.profile}</span>
                  </div>
                ))}
              </div>
            )}
            {usersWithStatus.length === 0 && (
              <div style={{ textAlign: "center", padding: 20, color: colors.textMuted, fontSize: 12 }}>Carregando...</div>
            )}
          </DialogContent>
        </Dialog>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <MainContainer className={classes.page}>
      <div className={classes.root}>
        {!shouldHideHeader && (
          <div className={classes.head}>
            <span className={classes.title}>Dashboard Geral — {groupName}</span>
          </div>
        )}
        {body}
      </div>
    </MainContainer>
  );
};

const DashboardGeral = () => <DashboardGeralBody />;

export default DashboardGeral;
